import type {
  ApiKey,
  ApiKeySecret,
  ApiKeyUsage,
  ApiKeysClient,
  CreateApiKeyPayload,
  UpdateApiKeyPayload
} from './apiKeys.types'
import { maskSecret, secretPrefix } from './format'
import { API_KEY_NAME_EXISTS_CODE } from './errors'
import { expirationToDate } from './scopeCatalog'

/** Versioned: records persisted under the previous key still carry `environment: 'live'`. */
export const MOCK_STORAGE_KEY = 'dp-mock-api-keys-v2'

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

interface MockOptions {
  /** Simulated latency in ms; 0 for tests. */
  latency?: number
  /** Where to persist state; `null` keeps it in memory only. */
  storage?: Storage | null
  /** Clock, injectable for tests. */
  now?: () => number
}

interface StoredState {
  keys: ApiKey[]
  counter: number
}

function randomSecret(): string {
  let out = ''
  while (out.length < 32) out += Math.random().toString(36).slice(2)
  return out.slice(0, 32)
}

/** Seed matching the API Keys list design frame. */
export function seedApiKeys(now = Date.now()): ApiKey[] {
  const created = new Date(now - 96 * DAY)
  return [
    {
      id: 'key_prod_reader',
      name: 'Production reader',
      maskedKey: 'sk_live_a1b2••••3f9c',
      environment: 'production',
      status: 'active',
      scopes: [
        'patients.read',
        'patients.details.read',
        'patients.vitals.read',
        'patients.risk_score.read',
        'reports.daily_risk.read'
      ],
      facilityIds: null,
      createdAt: created.toISOString(),
      issuedAt: created.toISOString(),
      createdBy: { name: 'A. Kováčová' },
      lastUsedAt: new Date(now - 3 * MINUTE).toISOString(),
      expiresAt: new Date(now + 3 * DAY).toISOString(),
      revokedAt: null
    },
    {
      id: 'key_sandbox_test',
      name: 'Sandbox test',
      maskedKey: 'sk_test_9x8y••••1a2b',
      environment: 'sandbox',
      status: 'active',
      scopes: [
        'patients.read',
        'patients.details.read',
        'patients.vitals.read',
        'patients.labs.read',
        'patients.risk_score.read',
        'patients.risk_drivers.read',
        'reports.daily_risk.read',
        'reports.daily_risk.export'
      ],
      facilityIds: null,
      createdAt: created.toISOString(),
      issuedAt: created.toISOString(),
      createdBy: { name: 'A. Kováčová' },
      lastUsedAt: new Date(now - HOUR).toISOString(),
      expiresAt: null,
      revokedAt: null
    },
    {
      id: 'key_legacy_import',
      name: 'Legacy import',
      maskedKey: 'sk_live_7d6e••••8c4d',
      environment: 'production',
      status: 'inactive',
      scopes: ['reports.daily_risk.read', 'reports.daily_risk.content.read'],
      facilityIds: null,
      createdAt: new Date(now - 255 * DAY).toISOString(),
      issuedAt: new Date(now - 255 * DAY).toISOString(),
      createdBy: { name: 'M. Novak' },
      lastUsedAt: null,
      expiresAt: null,
      revokedAt: null
    },
    {
      id: 'key_old_partner',
      name: 'Old partner key',
      maskedKey: 'sk_live_0000••••0000',
      environment: 'production',
      status: 'inactive',
      scopes: ['patients.read', 'patients.risk_score.read', 'patients.vitals.read'],
      facilityIds: null,
      createdAt: new Date(now - 400 * DAY).toISOString(),
      issuedAt: new Date(now - 400 * DAY).toISOString(),
      createdBy: { name: 'M. Novak' },
      lastUsedAt: new Date(now - 180 * DAY).toISOString(),
      expiresAt: null,
      revokedAt: new Date(now - 179 * DAY).toISOString()
    }
  ]
}

/** Deterministic pseudo usage per key so the Usage tab has something to show. */
function mockUsage(key: ApiKey): ApiKeyUsage | null {
  if (key.status !== 'active' || !key.lastUsedAt) {
    return {
      periodDays: 30,
      apiCalls: { total: 0, previousTotal: null },
      latency: { avgMs: null, previousAvgMs: null },
      successRate: { percent: null, previousPercent: null, errorCount: 0 },
      keys: { generated: 0, active: 0, inactive: 0, deleted: 0 },
      series: [],
      byEvent: []
    }
  }
  const seed = Array.from(key.id).reduce((acc, ch) => acc + ch.charCodeAt(0), 0)
  const total = 4_000 + (seed % 37) * 550
  return {
    periodDays: 30,
    apiCalls: { total, previousTotal: Math.round(total * 0.89) },
    latency: { avgMs: 120 + (seed % 60), previousAvgMs: 128 + (seed % 60) },
    successRate: { percent: 99.2, previousPercent: 98.9, errorCount: Math.round(total * 0.008) },
    keys: { generated: 1, active: 1, inactive: 0, deleted: 0 },
    series: [],
    byEvent: []
  }
}

/**
 * In-memory API keys client for local development and tests
 * (`VITE_DEVELOPER_PLATFORM_MOCK_API=true`). State survives a refresh
 * through `sessionStorage`; ids are deterministic (`key_<n>`).
 */
export class MockApiKeysClient implements ApiKeysClient {
  private state: StoredState
  private readonly latency: number
  private readonly storage: Storage | null
  private readonly now: () => number

  constructor(options: MockOptions = {}) {
    this.latency = options.latency ?? 300
    this.now = options.now ?? Date.now
    this.storage = options.storage === undefined ? safeSessionStorage() : options.storage
    this.state = this.load()
  }

  private load(): StoredState {
    try {
      const raw = this.storage?.getItem(MOCK_STORAGE_KEY)
      if (raw) {
        const parsed = JSON.parse(raw) as StoredState
        if (Array.isArray(parsed.keys)) return parsed
      }
    } catch {
      // fall through to a fresh seed
    }
    return { keys: seedApiKeys(this.now()), counter: 1 }
  }

  private persist(): void {
    try {
      this.storage?.setItem(MOCK_STORAGE_KEY, JSON.stringify(this.state))
    } catch {
      // storage full or unavailable: keep working in memory
    }
  }

  private delay<T>(value: T): Promise<T> {
    if (this.latency <= 0) return Promise.resolve(value)
    return new Promise(resolve => setTimeout(() => resolve(value), this.latency))
  }

  private find(id: string): ApiKey {
    const key = this.state.keys.find(k => k.id === id)
    if (!key) throw new Error(`API key ${id} not found`)
    return key
  }

  private replace(next: ApiKey): ApiKey {
    this.state.keys = this.state.keys.map(k => (k.id === next.id ? next : k))
    this.persist()
    return next
  }

  /** Test helper: drop everything, including the seed. */
  reset(keys: ApiKey[] = []): void {
    this.state = { keys, counter: 1 }
    this.persist()
  }

  list(): Promise<ApiKey[]> {
    return this.delay([...this.state.keys])
  }

  get(id: string): Promise<ApiKey | null> {
    return this.delay(this.state.keys.find(k => k.id === id) ?? null)
  }

  create(payload: CreateApiKeyPayload): Promise<ApiKeySecret> {
    const name = payload.name.trim()
    // Live key names are unique per organisation; mirror the backend's
    // `integration_api_key_name_exists` rejection (same `code` as core's ApiError).
    if (this.state.keys.some(k => k.name === name)) {
      return Promise.reject(
        Object.assign(new Error(`Organization already has an API key with name '${name}'`), {
          status: 400,
          code: API_KEY_NAME_EXISTS_CODE
        })
      )
    }
    const secret = `${secretPrefix(payload.environment)}${randomSecret()}`
    const id = `key_${this.state.counter++}`
    const key: ApiKey = {
      id,
      name: payload.name.trim(),
      maskedKey: maskSecret(secret),
      environment: payload.environment,
      status: 'active',
      scopes: [...payload.scopes],
      facilityIds: null,
      createdAt: new Date(this.now()).toISOString(),
      issuedAt: new Date(this.now()).toISOString(),
      createdBy: { name: 'You' },
      lastUsedAt: null,
      expiresAt: expirationToDate(payload.expiration, new Date(this.now())),
      revokedAt: null
    }
    this.state.keys = [key, ...this.state.keys]
    this.persist()
    return this.delay({ key, secret })
  }

  async update(id: string, payload: UpdateApiKeyPayload): Promise<ApiKey> {
    const current = this.find(id)
    const next: ApiKey = {
      ...current,
      name: payload.name?.trim() || current.name,
      scopes: payload.scopes ? [...payload.scopes] : current.scopes,
      expiresAt: payload.expiration
        ? expirationToDate(payload.expiration, new Date(this.now()))
        : current.expiresAt
    }
    return this.delay(this.replace(next))
  }

  async rotate(id: string): Promise<ApiKeySecret> {
    const current = this.find(id)
    const secret = `${secretPrefix(current.environment)}${randomSecret()}`
    const key = this.replace({
      ...current,
      maskedKey: maskSecret(secret),
      issuedAt: new Date(this.now()).toISOString()
    })
    return this.delay({ key, secret })
  }

  async setEnabled(id: string, enabled: boolean): Promise<ApiKey> {
    const current = this.find(id)
    return this.delay(
      this.replace({ ...current, status: enabled ? 'active' : 'inactive', revokedAt: null })
    )
  }

  async remove(id: string): Promise<void> {
    this.find(id)
    this.state.keys = this.state.keys.filter(k => k.id !== id)
    this.persist()
    return this.delay(undefined)
  }

  getUsage(id: string): Promise<ApiKeyUsage | null> {
    const key = this.state.keys.find(k => k.id === id)
    return this.delay(key ? mockUsage(key) : null)
  }
}

function safeSessionStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch {
    return null
  }
}
